import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_LEFT_PRESSED, MASK_RIGHT_PRESSED, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export type DesensitizeCue = 'left' | 'right' | 'decoy';
export type DesensitizeObject = 'tarp' | 'goose' | 'umbrella' | 'bucket';
export interface DesensitizePrompt {
  tick: number;
  cue: DesensitizeCue;
  object: DesensitizeObject;
  resolved: boolean;
}

const OBJECTS: readonly DesensitizeObject[] = ['tarp', 'goose', 'umbrella', 'bucket'];

/** Composure reactions: counter real spooks and sit through harmless noise. */
export class DesensitizeSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly prompts: DesensitizePrompt[];
  tick = 0;
  correct = 0;
  mistakes = 0;
  calm = 1;
  private cursor = 0;

  constructor(readonly tier: number, seed: number, mods?: Record<string, number>) {
    const rng = new DerbyRngV1(seed).fork('desensitize');
    const prompts: DesensitizePrompt[] = [];
    let at = 30;
    while (at < this.durationTicks - 12) {
      const decoyChance = tier >= 2 ? 0.25 + tier * 0.04 : 0.1;
      const cue: DesensitizeCue = rng.chance(decoyChance) ? 'decoy' : rng.chance(0.5) ? 'left' : 'right';
      prompts.push({ tick: at, cue, object: rng.pick(OBJECTS), resolved: false });
      at += Math.round(rng.range(0.9 - tier * 0.04, 1.35 - tier * 0.04) / DRILL_TICK_S);
    }
    this.prompts = prompts;
    this.drainMul = mods?.penDrainMul ?? 1;
  }

  readonly drainMul: number;
  get finished(): boolean { return this.tick >= this.durationTicks || this.calm <= 0; }
  nextPrompt(): DesensitizePrompt | null { return this.prompts[this.cursor] ?? null; }
  nextPromptIndex(): number { return this.cursor; }
  timingWindowTicks(): number { return Math.max(4, 9 - this.tier); }

  step(mask: number): void {
    if (this.finished) return;
    const window = this.timingWindowTicks();
    const left = (mask & MASK_LEFT_PRESSED) !== 0;
    const right = (mask & MASK_RIGHT_PRESSED) !== 0;
    const input = left === right ? null : left ? 'left' : 'right';
    const prompt = this.prompts[this.cursor];
    if (input) {
      const expected = prompt?.cue === 'left' ? 'right' : prompt?.cue === 'right' ? 'left' : null;
      if (prompt && Math.abs(this.tick - prompt.tick) <= window && input === expected) {
        prompt.resolved = true; this.correct += 1; this.cursor += 1;
      } else {
        this.mistakes += 1; this.calm = Math.max(0, this.calm - 0.14 * this.drainMul);
      }
    }
    const due = this.prompts[this.cursor];
    if (due && this.tick > due.tick + window) {
      if (due.cue === 'decoy') this.correct += 1;
      else { this.mistakes += 1; this.calm = Math.max(0, this.calm - 0.12 * this.drainMul); }
      due.resolved = true; this.cursor += 1;
    }
    this.tick += 1;
  }

  score(): SessionScore {
    const accuracy = this.prompts.length ? this.correct / this.prompts.length : 0;
    const raw = Math.max(0, Math.min(1, 0.85 * accuracy + 0.15 * this.calm));
    return { raw, detail: `${this.correct}/${this.prompts.length} calm reads · ${this.mistakes} mistakes`, failed: this.calm <= 0 };
  }
}

export const DESENSITIZE_DEF: MinigameDef = {
  id: 'desensitize', name: 'Desensitization Line', stat: 'heart', energyCost: 4,
  create: (tier, seed, mods) => new DesensitizeSession(tier, seed, mods),
};
